import type {
  Agent,
  AgentOutputType,
  ToolsToFinalOutputResult,
} from '../agent';
import { setAgentToolParentRunConfigOnDetails } from '../agent-tool-run-config';
import { consumeAgentToolRunResult } from '../agent-tool-run-results';
import { ToolCallError, ToolTimeoutError, UserError } from '../errors';
import { getTransferMessage, type HandoffInputData } from '../handoff';
import {
  RunHandoffCallItem,
  RunHandoffOutputItem,
  type RunItem,
  RunToolApprovalItem,
  RunToolCallOutputItem,
} from '../items';
import logger from '../logger';
import type { ModelResponse } from '../model';
import type { RunResult } from '../result';
import type { RunConfig, Runner, ToolErrorFormatter } from '../run';
import type { RunContext } from '../run-context';
import type { RunState } from '../run-state';
import {
  type FunctionToolResult,
  invokeFunctionTool,
  resolveToolResult,
  type Tool,
} from '../tool';
import {
  getFunctionToolQualifiedName,
  matchesFunctionToolName,
} from '../tool-identity';
import { getCurrentTrace } from '../tracing/context';
import { withFunctionSpan, withHandoffSpan } from '../tracing/create-spans';
import type { FunctionSpanData, Span } from '../tracing/spans';
import type { AgentInputItem, UnknownContext } from '../types';
import type {
  FunctionCallResultItem,
  ToolCallStructuredOutput,
  ToolOutputFileContent,
  ToolOutputImage,
  ToolOutputText,
} from '../types/protocol';
import * as protocol from '../types/protocol';
import { encodeUint8ArrayToBase64 } from '../utils/base64';
import { toSmartString } from '../utils/smart-string';
import {
  runToolInputGuardrails,
  runToolOutputGuardrails,
} from '../utils/tool-guardrails';
import { isZodObject } from '../utils/type-guards';
import {
  resolveApprovalRejectionMessage,
  TOOL_APPROVAL_REJECTION_MESSAGE,
} from './approval-rejection';
import { SingleStepResult } from './steps';
import type { ToolRunFunction, ToolRunHandoff } from './types';

const NOT_FINAL_OUTPUT: ToolsToFinalOutputResult = {
  isFinalOutput: false,
  isInterrupted: undefined,
};

type ParseToolArgumentsResult =
  | { success: true; args: any }
  | { success: false; error: Error };

type FunctionToolCallDeps<TContext = UnknownContext> = {
  agent: Agent<TContext, any>;
  runner: Runner;
  state: RunState<TContext, Agent<TContext, any>>;
  toolErrorFormatter?: ToolErrorFormatter;
  agentToolParentRunConfig?: Partial<RunConfig>;
};

/**
 * @internal
 * Runs every function tool call requested by the model and returns their outputs alongside
 * the `RunItem` instances that should be appended to history.
 */
export async function executeFunctionToolCalls<TContext = UnknownContext>(
  agent: Agent<TContext, any>,
  toolRuns: ToolRunFunction<TContext>[],
  runner: Runner,
  state: RunState<TContext, Agent<TContext, any>>,
  toolErrorFormatter?: ToolErrorFormatter,
  agentToolParentRunConfig?: Partial<RunConfig>,
): Promise<FunctionToolResult<TContext>[]> {
  const deps: FunctionToolCallDeps<TContext> = {
    agent,
    runner,
    state,
    toolErrorFormatter,
    agentToolParentRunConfig,
  };

  try {
    const results = await executeFunctionToolCallSchedule(deps, toolRuns);
    return results;
  } catch (e: unknown) {
    if (e instanceof ToolTimeoutError) {
      e.state ??= state;
      throw e;
    }

    throw new ToolCallError(
      `Failed to run function tools: ${e}`,
      e as Error,
      state,
    );
  }
}

type IndexedToolRun<TContext = UnknownContext> = {
  index: number;
  toolRun: ToolRunFunction<TContext>;
};

async function executeFunctionToolCallSchedule<TContext = UnknownContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRuns: ToolRunFunction<TContext>[],
): Promise<FunctionToolResult<TContext>[]> {
  const results = new Array<FunctionToolResult<TContext> | undefined>(
    toolRuns.length,
  );
  const parallelRuns: IndexedToolRun<TContext>[] = [];
  const serialGroups = new Map<string, IndexedToolRun<TContext>[]>();

  for (const [index, toolRun] of toolRuns.entries()) {
    const key = getSerialConcurrencyKey(toolRun);
    if (!key) {
      parallelRuns.push({ index, toolRun });
      continue;
    }
    const group = serialGroups.get(key);
    if (group) {
      group.push({ index, toolRun });
    } else {
      serialGroups.set(key, [{ index, toolRun }]);
    }
  }

  await Promise.all([
    ...parallelRuns.map(async ({ index, toolRun }) => {
      results[index] = await executeFunctionToolCall(deps, toolRun);
    }),
    ...[...serialGroups.values()].map(async (group) => {
      for (const { index, toolRun } of group) {
        results[index] = await executeFunctionToolCall(deps, toolRun);
      }
    }),
  ]);

  return results.map((result, index) => {
    if (!result) {
      throw new Error(`Missing function tool result at index ${index}`);
    }
    return result;
  });
}

function getSerialConcurrencyKey<TContext>(
  toolRun: ToolRunFunction<TContext>,
): string | null {
  const execution = toolRun.tool.execution;
  if (execution?.mode !== 'serial') {
    return null;
  }
  return execution.concurrencyKey;
}

async function executeFunctionToolCall<TContext = UnknownContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
): Promise<FunctionToolResult<TContext>> {
  if (toolRun.tool.requiresApprovalValidation) {
    const inputResult = await parseApprovalToolInput(deps, toolRun);
    if (!inputResult.success) {
      return inputResult.result;
    }

    const approvalOutcome = await handleFunctionApproval(
      deps,
      toolRun,
      inputResult.args,
    );
    if (approvalOutcome !== 'approved') {
      return approvalOutcome;
    }
    return runApprovedFunctionTool(deps, toolRun);
  }

  const parseResult = parseToolArguments(toolRun);

  // Handle parse errors gracefully instead of crashing
  if (!parseResult.success) {
    return buildParseErrorResult(deps, toolRun, parseResult.error);
  }

  const approvalOutcome = await handleFunctionApproval(
    deps,
    toolRun,
    parseResult.args,
  );
  if (approvalOutcome !== 'approved') {
    return approvalOutcome;
  }
  return runApprovedFunctionTool(deps, toolRun);
}

function parseToolArguments<TContext>(
  toolRun: ToolRunFunction<TContext>,
): ParseToolArgumentsResult {
  const toolName = getFunctionToolIdentity(toolRun);
  try {
    let parsedArgs: any = toolRun.toolCall.arguments;
    if (toolRun.tool.parameters) {
      if (isZodObject(toolRun.tool.parameters)) {
        parsedArgs = toolRun.tool.parameters.parse(parsedArgs);
      } else {
        parsedArgs = JSON.parse(parsedArgs);
      }
    }
    return { success: true, args: parsedArgs };
  } catch (error) {
    logger.debug(`Failed to parse tool arguments for ${toolName}: ${error}`);
    return { success: false, error: error as Error };
  }
}

function buildParseErrorResult<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
  error: Error,
): FunctionToolResult<TContext> {
  const errorMessage = `An error occurred while parsing tool arguments. Please try again with valid JSON. Error: ${error.message}`;
  return {
    type: 'function_output',
    tool: toolRun.tool,
    output: errorMessage,
    runItem: new RunToolCallOutputItem(
      getToolCallOutputItem(toolRun.toolCall, errorMessage, { isError: true }),
      deps.agent,
      errorMessage,
    ),
  };
}

type ParseApprovalToolInputResult<TContext = UnknownContext> =
  | { success: true; args: unknown }
  | { success: false; result: FunctionToolResult<TContext> };

async function parseApprovalToolInput<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
): Promise<ParseApprovalToolInputResult<TContext>> {
  if (!toolRun.tool.parseInput || !toolRun.tool.handleError) {
    throw new UserError(
      'Tool requires approval validation but does not expose input parsing hooks.',
    );
  }

  try {
    return {
      success: true,
      args: await toolRun.tool.parseInput(
        deps.state._context,
        toolRun.toolCall.arguments,
        {
          toolCall: toolRun.toolCall,
        },
      ),
    };
  } catch (error) {
    logger.debug(
      `Failed to validate tool arguments for approval ${getFunctionToolIdentity(toolRun)}: ${error}`,
    );
    return {
      success: false,
      result: await buildToolInputErrorResult(deps, toolRun, error),
    };
  }
}

async function buildToolInputErrorResult<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
  error: unknown,
): Promise<FunctionToolResult<TContext>> {
  if (!toolRun.tool.handleError) {
    throw error;
  }

  const rawToolOutput = await toolRun.tool.handleError(
    deps.state._context,
    error,
    {
      toolCall: toolRun.toolCall,
    },
  );
  const resolvedToolOutput = resolveToolResult(rawToolOutput);
  const toolOutput = resolvedToolOutput.output;

  return {
    type: 'function_output',
    tool: toolRun.tool,
    output: toolOutput,
    runItem: new RunToolCallOutputItem(
      getToolCallOutputItem(toolRun.toolCall, toolOutput, {
        isError: true,
        display: resolvedToolOutput.display,
      }),
      deps.agent,
      toolOutput,
    ),
  };
}

/**
 * @internal
 * Normalizes tool outputs once so downstream code works with fully structured protocol items.
 * Doing this here keeps API surface stable even when providers add new shapes.
 */
export function getToolCallOutputItem(
  toolCall: protocol.FunctionCallItem,
  output: string | unknown,
  options: { isError?: boolean; display?: unknown } = {},
): FunctionCallResultItem {
  const maybeStructuredOutputs = normalizeStructuredToolOutputs(output);
  const status = options.isError ? 'incomplete' : 'completed';
  const display =
    options.display !== undefined ? { display: options.display } : {};

  if (maybeStructuredOutputs) {
    const structuredItems = maybeStructuredOutputs.map(
      convertStructuredToolOutputToInputItem,
    );

    return {
      type: 'function_call_result',
      name: toolCall.name,
      ...(typeof toolCall.namespace === 'string'
        ? { namespace: toolCall.namespace }
        : {}),
      callId: toolCall.callId,
      status,
      output: structuredItems,
      ...display,
    };
  }

  return {
    type: 'function_call_result',
    name: toolCall.name,
    ...(typeof toolCall.namespace === 'string'
      ? { namespace: toolCall.namespace }
      : {}),
    callId: toolCall.callId,
    status,
    output: {
      type: 'text',
      text: toSmartString(output),
    },
    ...display,
  };
}

async function handleFunctionApproval<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
  parsedArgs: any,
): Promise<'approved' | FunctionToolResult<TContext>> {
  const { state } = deps;
  const toolName = getFunctionToolIdentity(toolRun);
  const needsApproval = await toolRun.tool.needsApproval(
    state._context,
    parsedArgs,
    toolRun.toolCall.callId,
  );

  if (!needsApproval) {
    return 'approved';
  }

  const approval = state._context.isToolApproved({
    toolName,
    callId: toolRun.toolCall.callId,
  });

  if (approval === false) {
    state.clearPendingAgentToolRun(toolName, toolRun.toolCall.callId);
    return await buildApprovalRejectionResult(deps, toolRun);
  }

  if (approval !== true) {
    return buildApprovalRequestResult(deps, toolRun);
  }

  return 'approved';
}

function getFunctionToolIdentity<TContext>(
  toolRun: ToolRunFunction<TContext>,
): string {
  return getFunctionToolQualifiedName(toolRun.tool) ?? toolRun.tool.name;
}

async function runApprovedFunctionTool<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
): Promise<FunctionToolResult<TContext>> {
  const { agent, runner, state, agentToolParentRunConfig } = deps;
  const toolName = getFunctionToolIdentity(toolRun);
  const traceToolName = getFunctionToolTraceName(toolRun);
  const invocationInput = getApprovedToolInvocationInput(
    state,
    toolName,
    toolRun.toolCall,
  );
  const invocationToolCall =
    invocationInput === toolRun.toolCall.arguments
      ? toolRun.toolCall
      : {
          ...toolRun.toolCall,
          arguments: invocationInput,
        };
  return withToolFunctionSpan(runner, traceToolName, async (span) => {
    if (span && runner.config.traceIncludeSensitiveData) {
      span.spanData.input = invocationInput;
    }

    try {
      const inputGuardrailResult = await runToolInputGuardrails({
        guardrails: toolRun.tool.inputGuardrails,
        context: state._context,
        agent,
        toolCall: invocationToolCall,
        onResult: (result) => {
          state._toolInputGuardrailResults.push(result);
        },
      });

      emitToolStart(
        runner,
        state._context,
        agent,
        toolRun.tool,
        invocationToolCall,
      );

      let toolOutput: unknown;
      let toolOutputIsError = false;
      let toolDisplay: unknown;
      if (inputGuardrailResult.type === 'reject') {
        toolOutput = inputGuardrailResult.message;
      } else {
        const resumeState = state.getPendingAgentToolRun(
          toolName,
          toolRun.toolCall.callId,
        );
        const toolDetails = {
          toolCall: invocationToolCall,
          resumeState,
        };
        setAgentToolParentRunConfigOnDetails(
          toolDetails,
          agentToolParentRunConfig ?? runner.config,
        );
        const rawToolOutput = await invokeFunctionTool({
          tool: toolRun.tool,
          runContext: state._context,
          input: invocationInput,
          details: toolDetails,
        });
        const resolvedToolOutput = resolveToolResult(rawToolOutput);
        toolOutput = resolvedToolOutput.output;
        toolOutputIsError = resolvedToolOutput.isError;
        toolDisplay = resolvedToolOutput.display;
        toolOutput = await runToolOutputGuardrails({
          guardrails: toolRun.tool.outputGuardrails,
          context: state._context,
          agent,
          toolCall: invocationToolCall,
          toolOutput,
          onResult: (result) => {
            state._toolOutputGuardrailResults.push(result);
          },
        });
      }
      const stringResult = toSmartString(toolOutput);

      emitToolEnd(
        runner,
        state._context,
        agent,
        toolRun.tool,
        stringResult,
        invocationToolCall,
      );

      if (span && runner.config.traceIncludeSensitiveData) {
        span.spanData.output = stringResult;
      }

      const functionResult: FunctionToolResult<TContext> = {
        type: 'function_output' as const,
        tool: toolRun.tool,
        output: toolOutput,
        runItem: new RunToolCallOutputItem(
          getToolCallOutputItem(toolRun.toolCall, toolOutput, {
            isError: toolOutputIsError,
            display: toolDisplay,
          }),
          agent,
          toolOutput,
        ),
      };

      const nestedRunResult = consumeAgentToolRunResult(toolRun.toolCall) as
        | RunResult<TContext, Agent<TContext, any>>
        | undefined;
      if (nestedRunResult) {
        functionResult.agentRunResult = nestedRunResult;
        const nestedInterruptions = nestedRunResult.interruptions;
        if (nestedInterruptions.length > 0) {
          functionResult.interruptions = nestedInterruptions;
          const nestedRunStateJson = nestedRunResult.state.toJSON();
          state.setPendingAgentToolRun(
            toolName,
            toolRun.toolCall.callId,
            JSON.stringify(nestedRunStateJson),
          );
        } else {
          state.clearPendingAgentToolRun(toolName, toolRun.toolCall.callId);
        }
      }

      return functionResult;
    } catch (error) {
      span?.setError({
        message: 'Error running tool',
        data: {
          tool_name: traceToolName,
          error: String(error),
        },
      });

      const errorResult = String(error);
      emitToolEnd(
        runner,
        state._context,
        agent,
        toolRun.tool,
        errorResult,
        invocationToolCall,
      );

      throw error;
    }
  });
}

function getApprovedToolInvocationInput<TContext>(
  state: RunState<TContext, Agent<TContext, any>>,
  toolName: string,
  toolCall: protocol.FunctionCallItem,
): string {
  const updatedInput = state._context.getApprovalUpdatedInput(
    toolName,
    toolCall.callId,
  );
  if (updatedInput === undefined) {
    return toolCall.arguments;
  }
  return typeof updatedInput === 'string'
    ? updatedInput
    : JSON.stringify(updatedInput);
}

function getFunctionToolTraceName<TContext>(
  toolRun: ToolRunFunction<TContext>,
): string {
  return getFunctionToolIdentity(toolRun);
}

type StructuredToolOutput =
  | ToolOutputText
  | ToolOutputImage
  | ToolOutputFileContent;

/**
 * Accepts whatever the tool returned and attempts to coerce it into the structured protocol
 * shapes we expose to downstream model adapters (input_text/input_image/input_file). Tools are
 * allowed to return either a single structured object or an array of them; anything else falls
 * back to the legacy string pipeline.
 */
function normalizeStructuredToolOutputs(
  output: unknown,
): StructuredToolOutput[] | null {
  if (Array.isArray(output)) {
    const structured: StructuredToolOutput[] = [];
    for (const item of output) {
      const normalized = normalizeStructuredToolOutput(item);
      if (!normalized) {
        return null;
      }
      structured.push(normalized);
    }
    return structured;
  }
  const normalized = normalizeStructuredToolOutput(output);
  return normalized ? [normalized] : null;
}

/**
 * Translates the normalized tool output into the protocol `input_*` items. This is the last hop
 * before we hand the data to model-specific adapters, so we generate the exact schema expected by
 * the protocol definitions.
 */
function convertStructuredToolOutputToInputItem(
  output: StructuredToolOutput,
): ToolCallStructuredOutput {
  if (output.type === 'text') {
    const result: protocol.InputText = {
      type: 'input_text',
      text: output.text,
    };
    if (output.providerData) {
      result.providerData = output.providerData;
    }
    return result;
  }
  if (output.type === 'image') {
    const result: protocol.InputImage = { type: 'input_image' };
    if (typeof output.detail === 'string' && output.detail.length > 0) {
      result.detail = output.detail;
    }
    if (typeof output.image === 'string' && output.image.length > 0) {
      result.image = output.image;
    } else if (isRecord(output.image)) {
      const imageObj = output.image as Record<string, any>;
      const inlineMediaType = getImageInlineMediaType(imageObj);
      if (isNonEmptyString(imageObj.url)) {
        result.image = imageObj.url;
      } else if (isNonEmptyString(imageObj.data)) {
        result.image =
          inlineMediaType && !imageObj.data.startsWith('data:')
            ? asDataUrl(imageObj.data, inlineMediaType)
            : imageObj.data;
      } else if (
        imageObj.data instanceof Uint8Array &&
        imageObj.data.length > 0
      ) {
        const base64 = encodeUint8ArrayToBase64(imageObj.data);
        result.image = asDataUrl(base64, inlineMediaType);
      } else {
        const referencedId =
          (isNonEmptyString(imageObj.fileId) && imageObj.fileId) ||
          (isNonEmptyString(imageObj.id) && imageObj.id) ||
          undefined;
        if (referencedId) {
          result.image = { id: referencedId };
        }
      }
    }
    if (output.providerData) {
      result.providerData = output.providerData;
    }
    return result;
  }

  if (output.type === 'file') {
    const result: protocol.InputFile = { type: 'input_file' };
    const fileValue = output.file;
    if (typeof fileValue === 'string') {
      result.file = fileValue;
    } else if (fileValue && typeof fileValue === 'object') {
      const record = fileValue as Record<string, any>;
      if ('data' in record && record.data) {
        const mediaType = record.mediaType ?? 'text/plain';
        if (typeof record.data === 'string') {
          result.file = asDataUrl(record.data, mediaType);
        } else {
          const base64 = encodeUint8ArrayToBase64(record.data);
          result.file = asDataUrl(base64, mediaType);
        }
      } else if (typeof record.url === 'string' && record.url.length > 0) {
        result.file = { url: record.url };
      } else {
        const referencedId =
          (typeof record.id === 'string' &&
            record.id.length > 0 &&
            record.id) ||
          (typeof record.fileId === 'string' && record.fileId.length > 0
            ? record.fileId
            : undefined);
        if (referencedId) {
          result.file = { id: referencedId };
        }
      }

      if (typeof record.filename === 'string' && record.filename.length > 0) {
        result.filename = record.filename;
      }
    }
    if (output.providerData) {
      result.providerData = output.providerData;
    }
    return result;
  }
  const exhaustiveCheck: never = output;
  return exhaustiveCheck;
}

async function buildApprovalRejectionResult<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
): Promise<FunctionToolResult<TContext>> {
  const { agent, runner, state, toolErrorFormatter } = deps;
  const toolName = getFunctionToolIdentity(toolRun);
  const traceToolName = getFunctionToolTraceName(toolRun);
  return withToolFunctionSpan(runner, traceToolName, async (span) => {
    const response = await resolveApprovalRejectionMessage({
      runContext: state._context,
      toolType: 'function',
      toolName,
      callId: toolRun.toolCall.callId,
      toolErrorFormatter,
    });
    const traceErrorMessage = runner.config.traceIncludeSensitiveData
      ? response
      : TOOL_APPROVAL_REJECTION_MESSAGE;

    span?.setError({
      message: traceErrorMessage,
      data: {
        tool_name: traceToolName,
        error: `Tool execution for ${toolRun.toolCall.callId} was manually rejected by user.`,
      },
    });

    if (span && runner.config.traceIncludeSensitiveData) {
      span.spanData.output = response;
    }
    return {
      type: 'function_output' as const,
      tool: toolRun.tool,
      output: response,
      runItem: new RunToolCallOutputItem(
        getToolCallOutputItem(toolRun.toolCall, response),
        agent,
        response,
      ),
    };
  });
}

async function withToolFunctionSpan<T>(
  runner: Runner,
  toolName: string,
  fn: (span?: Span<FunctionSpanData>) => Promise<T>,
): Promise<T> {
  if (runner.config.tracingDisabled || !getCurrentTrace()) {
    return fn();
  }

  return withFunctionSpan(async (span) => fn(span), {
    data: {
      name: toolName,
    },
  });
}

function buildApprovalRequestResult<TContext>(
  deps: FunctionToolCallDeps<TContext>,
  toolRun: ToolRunFunction<TContext>,
): FunctionToolResult<TContext> {
  return {
    type: 'function_approval' as const,
    tool: toolRun.tool,
    runItem: new RunToolApprovalItem(
      toolRun.toolCall,
      deps.agent,
      getFunctionToolIdentity(toolRun),
    ),
  };
}

function emitToolStart(
  runner: Runner,
  runContext: RunContext,
  agent: Agent<any, any>,
  tool: Tool<any>,
  toolCall: protocol.ToolCallItem,
): void {
  runner.emit('agent_tool_start', runContext, agent, tool, { toolCall });
  if (typeof agent.emit === 'function') {
    agent.emit('agent_tool_start', runContext, tool, { toolCall });
  }
}

function emitToolEnd(
  runner: Runner,
  runContext: RunContext,
  agent: Agent<any, any>,
  tool: Tool<any>,
  output: string,
  toolCall: protocol.ToolCallItem,
): void {
  runner.emit('agent_tool_end', runContext, agent, tool, output, { toolCall });
  if (typeof agent.emit === 'function') {
    agent.emit('agent_tool_end', runContext, tool, output, { toolCall });
  }
}

/**
 * Best-effort normalization of a single tool output item. If the object already matches the
 * protocol shape we simply cast it; otherwise we copy the recognised fields into the canonical
 * structure. Returning null lets the caller know we should revert to plain-string handling.
 */
function normalizeStructuredToolOutput(
  value: unknown,
): StructuredToolOutput | null {
  if (!isRecord(value)) {
    return null;
  }
  const type = value.type;
  if (type === 'text' && typeof value.text === 'string') {
    const output: ToolOutputText = { type: 'text', text: value.text };
    if (isRecord(value.providerData)) {
      output.providerData = value.providerData;
    }
    return output;
  }

  if (type === 'image') {
    const output: ToolOutputImage = { type: 'image' };

    let imageString: string | undefined;
    let imageFileId: string | undefined;
    const fallbackImageMediaType = getImageInlineMediaType(value);

    const imageField = value.image;
    if (typeof imageField === 'string' && imageField.length > 0) {
      imageString = imageField;
    } else if (isRecord(imageField)) {
      const imageObj = imageField as Record<string, any>;
      const inlineMediaType =
        getImageInlineMediaType(imageObj) ?? fallbackImageMediaType;
      if (isNonEmptyString(imageObj.url)) {
        imageString = imageObj.url;
      } else if (isNonEmptyString(imageObj.data)) {
        imageString = toInlineImageString(imageObj.data, inlineMediaType);
      } else if (
        imageObj.data instanceof Uint8Array &&
        imageObj.data.length > 0
      ) {
        imageString = toInlineImageString(imageObj.data, inlineMediaType);
      }

      if (!imageString) {
        const candidateId =
          (isNonEmptyString(imageObj.fileId) && imageObj.fileId) ||
          (isNonEmptyString(imageObj.id) && imageObj.id) ||
          undefined;
        if (candidateId) {
          imageFileId = candidateId;
        }
      }
    }

    if (
      !imageString &&
      typeof value.imageUrl === 'string' &&
      value.imageUrl.length > 0
    ) {
      imageString = value.imageUrl;
    }
    if (
      !imageFileId &&
      typeof value.fileId === 'string' &&
      value.fileId.length > 0
    ) {
      imageFileId = value.fileId;
    }

    if (
      !imageString &&
      typeof value.data === 'string' &&
      value.data.length > 0
    ) {
      imageString = fallbackImageMediaType
        ? toInlineImageString(value.data, fallbackImageMediaType)
        : value.data;
    } else if (
      !imageString &&
      value.data instanceof Uint8Array &&
      value.data.length > 0
    ) {
      imageString = toInlineImageString(value.data, fallbackImageMediaType);
    }
    if (typeof value.detail === 'string' && value.detail.length > 0) {
      output.detail = value.detail;
    }

    if (imageString) {
      output.image = imageString;
    } else if (imageFileId) {
      output.image = { fileId: imageFileId };
    } else {
      return null;
    }

    if (isRecord(value.providerData)) {
      output.providerData = value.providerData;
    }
    return output;
  }

  if (type === 'file') {
    const fileValue = normalizeFileValue(value);
    if (!fileValue) {
      return null;
    }

    const output: ToolOutputFileContent = { type: 'file', file: fileValue };

    if (isRecord(value.providerData)) {
      output.providerData = value.providerData;
    }
    return output;
  }

  return null;
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function getImageInlineMediaType(
  value: Record<string, any>,
): string | undefined {
  if (isNonEmptyString(value.mediaType)) {
    return value.mediaType;
  }
  if (isNonEmptyString((value as any).mimeType)) {
    return (value as any).mimeType;
  }
  return undefined;
}

function asDataUrl(base64: string, mediaType?: string): string {
  return mediaType ? `data:${mediaType};base64,${base64}` : base64;
}

function toInlineImageString(
  data: string | Uint8Array,
  mediaType?: string,
): string {
  if (typeof data === 'string') {
    if (mediaType && !data.startsWith('data:')) {
      return asDataUrl(data, mediaType);
    }
    return data;
  }
  const base64 = encodeUint8ArrayToBase64(data);
  return asDataUrl(base64, mediaType);
}

type FileReferenceValue = ToolOutputFileContent['file'];
function normalizeFileValue(
  value: Record<string, any>,
): FileReferenceValue | null {
  const directFile = value.file;
  if (typeof directFile === 'string' && directFile.length > 0) {
    return directFile;
  }

  const normalizedObject = normalizeFileObjectCandidate(directFile);
  if (normalizedObject) {
    return normalizedObject;
  }

  const legacyValue = normalizeLegacyFileValue(value);
  if (legacyValue) {
    return legacyValue;
  }

  return null;
}

function normalizeFileObjectCandidate(
  value: unknown,
): FileReferenceValue | null {
  if (!isRecord(value)) {
    return null;
  }

  if ('data' in value && value.data !== undefined) {
    const dataValue = value.data;
    const hasStringData = typeof dataValue === 'string' && dataValue.length > 0;
    const hasBinaryData =
      dataValue instanceof Uint8Array && dataValue.length > 0;
    if (!hasStringData && !hasBinaryData) {
      return null;
    }

    if (
      !isNonEmptyString(value.mediaType) ||
      !isNonEmptyString(value.filename)
    ) {
      return null;
    }

    return {
      data:
        typeof dataValue === 'string' ? dataValue : new Uint8Array(dataValue),
      mediaType: value.mediaType,
      filename: value.filename,
    };
  }

  if (isNonEmptyString(value.url)) {
    const result: { url: string; filename?: string } = { url: value.url };
    if (isNonEmptyString(value.filename)) {
      result.filename = value.filename;
    }
    return result;
  }

  const referencedId =
    (isNonEmptyString(value.id) && value.id) ||
    (isNonEmptyString(value.fileId) && (value.fileId as string));
  if (referencedId) {
    const result: { id: string; filename?: string } = { id: referencedId };
    if (isNonEmptyString(value.filename)) {
      result.filename = value.filename;
    }
    return result;
  }

  return null;
}

function normalizeLegacyFileValue(
  value: Record<string, any>,
): FileReferenceValue | null {
  const filename =
    typeof value.filename === 'string' && value.filename.length > 0
      ? value.filename
      : undefined;
  const mediaType =
    typeof value.mediaType === 'string' && value.mediaType.length > 0
      ? value.mediaType
      : undefined;

  if (typeof value.fileData === 'string' && value.fileData.length > 0) {
    if (!mediaType || !filename) {
      return null;
    }
    return { data: value.fileData, mediaType, filename };
  }

  if (value.fileData instanceof Uint8Array && value.fileData.length > 0) {
    if (!mediaType || !filename) {
      return null;
    }
    return { data: new Uint8Array(value.fileData), mediaType, filename };
  }

  if (typeof value.fileUrl === 'string' && value.fileUrl.length > 0) {
    const result: { url: string; filename?: string } = { url: value.fileUrl };
    if (filename) {
      result.filename = filename;
    }
    return result;
  }

  if (typeof value.fileId === 'string' && value.fileId.length > 0) {
    const result: { id: string; filename?: string } = { id: value.fileId };
    if (filename) {
      result.filename = filename;
    }
    return result;
  }

  return null;
}

/**
 * Collects approval interruptions from tool execution results and any additional
 * RunItems (e.g., shell approval placeholders).
 */
export function collectInterruptions<TContext = UnknownContext>(
  toolResults: FunctionToolResult<TContext>[],
  additionalItems: RunItem[] = [],
): RunToolApprovalItem[] {
  const interruptions: RunToolApprovalItem[] = [];

  for (const item of additionalItems) {
    if (item instanceof RunToolApprovalItem) {
      interruptions.push(item);
    }
  }

  for (const result of toolResults) {
    if (result.runItem instanceof RunToolApprovalItem) {
      interruptions.push(result.runItem);
    }

    if (result.type === 'function_output') {
      if (Array.isArray(result.interruptions)) {
        interruptions.push(...result.interruptions);
      } else if (result.agentRunResult) {
        const nestedInterruptions = result.agentRunResult.interruptions;
        if (nestedInterruptions.length > 0) {
          interruptions.push(...nestedInterruptions);
        }
      }
    }
  }

  return interruptions;
}

/**
 * @internal
 * Determines whether tool executions produced a final agent output, triggered an interruption,
 * or whether the agent loop should continue collecting more responses.
 */
export async function checkForFinalOutputFromTools<
  TContext,
  TOutput extends AgentOutputType,
>(
  agent: Agent<TContext, TOutput>,
  toolResults: FunctionToolResult<TContext>[],
  state: RunState<TContext, Agent<TContext, TOutput>>,
  additionalInterruptions: RunItem[] = [],
): Promise<ToolsToFinalOutputResult> {
  if (toolResults.length === 0 && additionalInterruptions.length === 0) {
    return NOT_FINAL_OUTPUT;
  }

  const interruptions = collectInterruptions(
    toolResults,
    additionalInterruptions,
  );

  if (interruptions.length > 0) {
    return {
      isFinalOutput: false,
      isInterrupted: true,
      interruptions,
    };
  }

  if (agent.toolUseBehavior === 'run_llm_again') {
    return NOT_FINAL_OUTPUT;
  }

  const firstToolResult = toolResults[0];
  if (agent.toolUseBehavior === 'stop_on_first_tool') {
    if (firstToolResult?.type === 'function_output') {
      const stringOutput = toSmartString(firstToolResult.output);
      return {
        isFinalOutput: true,
        isInterrupted: undefined,
        finalOutput: stringOutput,
      };
    }
    return NOT_FINAL_OUTPUT;
  }

  const toolUseBehavior = agent.toolUseBehavior;
  if (typeof toolUseBehavior === 'object') {
    const stoppingTool = toolResults.find((r) => {
      return toolUseBehavior.stopAtToolNames.some((toolName) =>
        matchesFunctionToolName(r.tool, toolName),
      );
    });
    if (stoppingTool?.type === 'function_output') {
      const stringOutput = toSmartString(stoppingTool.output);
      return {
        isFinalOutput: true,
        isInterrupted: undefined,
        finalOutput: stringOutput,
      };
    }
    return NOT_FINAL_OUTPUT;
  }

  if (typeof toolUseBehavior === 'function') {
    return toolUseBehavior(state._context, toolResults as FunctionToolResult[]);
  }

  throw new UserError(`Invalid toolUseBehavior: ${toolUseBehavior}`, state);
}

/**
 * @internal
 * Drives handoff calls by invoking the downstream agent and capturing any generated items so
 * the current agent can continue with the new context.
 */
export async function executeHandoffCalls<
  TContext,
  TOutput extends AgentOutputType,
>(
  agent: Agent<TContext, TOutput>,
  originalInput: string | AgentInputItem[],
  preStepItems: RunItem[],
  newStepItems: RunItem[],
  newResponse: ModelResponse,
  runHandoffs: ToolRunHandoff[],
  runner: Runner,
  runContext: RunContext<TContext>,
): Promise<import('./steps').SingleStepResult> {
  newStepItems = [...newStepItems];

  if (runHandoffs.length === 0) {
    logger.warn(
      'Incorrectly called executeHandoffCalls with no handoffs. This should not happen. Moving on.',
    );
    return new SingleStepResult(
      originalInput,
      newResponse,
      preStepItems,
      newStepItems,
      {
        type: 'next_step_run_again',
      },
    );
  }

  if (runHandoffs.length > 1) {
    const ignoredCallIds = new Set(
      runHandoffs.slice(1).map((handoff) => handoff.toolCall.callId),
    );
    // Drop ignored handoff requests from the step so they never persist to history.
    newStepItems = newStepItems.filter(
      (item) =>
        !(
          item instanceof RunHandoffCallItem &&
          ignoredCallIds.has(item.rawItem.callId)
        ),
    );
  }

  const actualHandoff = runHandoffs[0]!;

  return withHandoffSpan(
    async (handoffSpan) => {
      const handoff = actualHandoff.handoff;

      const newAgent = await handoff.onInvokeHandoff(
        runContext,
        actualHandoff.toolCall.arguments,
      );

      handoffSpan.spanData.to_agent = newAgent.name;

      if (runHandoffs.length > 1) {
        const requestedAgents = runHandoffs.map((h) => h.handoff.agentName);
        handoffSpan.setError({
          message: 'Multiple handoffs requested',
          data: {
            requested_agents: requestedAgents,
          },
        });
      }

      newStepItems.push(
        new RunHandoffOutputItem(
          getToolCallOutputItem(
            actualHandoff.toolCall,
            getTransferMessage(newAgent),
          ),
          agent,
          newAgent,
        ),
      );

      runner.emit('agent_handoff', runContext, agent, newAgent);
      agent.emit('agent_handoff', runContext, newAgent);

      const inputFilter =
        handoff.inputFilter ?? runner.config.handoffInputFilter;
      if (inputFilter) {
        logger.debug('Filtering inputs for handoff');

        if (typeof inputFilter !== 'function') {
          handoffSpan.setError({
            message: 'Invalid input filter',
            data: {
              details: 'not callable',
            },
          });
        }

        const handoffInputData: HandoffInputData = {
          inputHistory: Array.isArray(originalInput)
            ? [...originalInput]
            : originalInput,
          preHandoffItems: [...preStepItems],
          newItems: [...newStepItems],
          runContext,
        };

        const filtered = inputFilter(handoffInputData);

        originalInput = filtered.inputHistory;
        preStepItems = filtered.preHandoffItems;
        newStepItems = filtered.newItems;
      }

      return new SingleStepResult(
        originalInput,
        newResponse,
        preStepItems,
        newStepItems,
        {
          type: 'next_step_handoff',
          newAgent,
        },
      );
    },
    {
      data: {
        from_agent: agent.name,
      },
    },
  );
}
